import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Table2, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientAnalyticsHistoryPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ dates: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/blood-analytics/history-table`).then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="analytics-history-page">
      <button onClick={() => navigate('/client/analytics')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> Analiticas de Sangre
      </button>
      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Historico de Resultados
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Resultados acumulados de todas tus analiticas</p>
      </div>

      {data.rows.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-history">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Table2 size={32} className="text-blue-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin datos historicos</p>
          <p className="text-sm text-gray-400 mt-1">Los resultados apareceran aqui cuando subas tus analiticas</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100/50 shadow-sm overflow-hidden">
          {/* Count banner */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white flex items-center gap-3">
            <Table2 size={20} />
            <div>
              <p className="font-bold text-sm">{data.rows.length} pruebas registradas</p>
              <p className="text-xs text-white/70">{data.dates.length} fecha{data.dates.length > 1 ? 's' : ''} de estudio</p>
            </div>
          </div>

          {/* Scroll controls */}
          {data.dates.length > 2 && (
            <div className="flex justify-end gap-2 px-4 pt-3">
              <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <ChevronLeft size={16} className="text-gray-500" />
              </button>
              <button onClick={() => scroll(1)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <ChevronRight size={16} className="text-gray-500" />
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto" ref={scrollRef}>
            <table className="w-full min-w-[600px]" data-testid="history-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wider p-4 sticky left-0 bg-white z-10 min-w-[200px]">
                    Prueba
                  </th>
                  {data.dates.map((d) => (
                    <th key={d} className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider p-4 min-w-[120px]">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <tr key={row.label} className={`border-b border-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`} data-testid={`history-row-${idx}`}>
                    <td className="p-4 sticky left-0 bg-inherit z-10">
                      <p className="text-sm font-semibold text-gray-800">{row.label}</p>
                      {row.unit && <p className="text-[10px] text-gray-400">{row.unit}</p>}
                    </td>
                    {data.dates.map((d) => {
                      const val = row.values[d];
                      return (
                        <td key={d} className="p-4 text-center">
                          {val ? (
                            <span className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                              {val}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAnalyticsHistoryPage;
